import React, { useEffect, useRef, useCallback } from 'react';
import { Box, Flex, Text, IconButton, Button, Icon, keyframes } from '@chakra-ui/react';
import { FiUserPlus, FiX, FiArrowRight } from 'react-icons/fi';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { removeAdminLeadAlert } from '../../../redux/freshLeadSlice';
import { playNotificationSound } from '../utils/notificationSound';
import { leadUrl } from './leadUrl';

const DISMISS_MS = 10000;

const slideIn = keyframes`
	from { opacity: 0; transform: translateX(24px) scale(0.98); }
	to { opacity: 1; transform: translateX(0) scale(1); }
`;

// One alert card. `bg.surface` is the opaque card surface — the earlier
// `bg.card` is not a theme token, which is why the card looked transparent.
const LeadAlertCard = ({ alert, onDismiss }) => {
	const navigate = useNavigate();
	const barRef = useRef(null);
	const timerRef = useRef(null);

	useEffect(() => {
		// Shrink the bar 100% -> 0% over DISMISS_MS.
		if (barRef.current) {
			barRef.current.style.transition = `width ${DISMISS_MS}ms linear`;
			// Reflow so the transition runs from the initial width.
			void barRef.current.offsetWidth;
			barRef.current.style.width = '0%';
		}

		timerRef.current = setTimeout(() => onDismiss(alert._id), DISMISS_MS);
		return () => clearTimeout(timerRef.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dismiss = () => {
		clearTimeout(timerRef.current);
		onDismiss(alert._id);
	};

	const handleOpen = () => {
		dismiss();
		navigate(leadUrl(alert._id));
	};

	return (
		<Box
			role='alert'
			position='relative'
			bg='bg.surface'
			border='1px solid'
			borderColor='border.default'
			borderRadius='xl'
			boxShadow='0 12px 32px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.18)'
			overflow='hidden'
			w={{ base: 'calc(100vw - 32px)', sm: '340px' }}
			mb={3}
			animation={`${slideIn} 0.28s ease-out`}
			sx={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
		>
			{/* accent edge */}
			<Box position='absolute' left={0} top={0} bottom={0} w='4px' bg='accent.solid' />

			<Flex pl={5} pr={3} pt={3} pb={3} gap={3} align='center'>
				<Flex
					w='38px'
					h='38px'
					borderRadius='full'
					bg='accent.subtle'
					color='text.accent'
					align='center'
					justify='center'
					flexShrink={0}
				>
					<Icon as={FiUserPlus} boxSize={5} />
				</Flex>

				<Box flex='1' minW={0}>
					<Text
						fontSize='10px'
						fontWeight='bold'
						letterSpacing='0.08em'
						textTransform='uppercase'
						color='text.accent'
					>
						New lead
					</Text>
					<Text
						fontSize='md'
						fontWeight='semibold'
						color='text.primary'
						noOfLines={1}
						lineHeight='short'
					>
						{alert.leadName}
					</Text>
					{alert.source && (
						<Text fontSize='xs' color='text.secondary' noOfLines={1}>
							via {alert.source}
						</Text>
					)}
				</Box>

				<Flex direction='column' align='flex-end' alignSelf='stretch' justify='space-between' flexShrink={0}>
					<IconButton
						icon={<Icon as={FiX} boxSize={4} />}
						size='xs'
						variant='ghost'
						color='text.secondary'
						aria-label='Dismiss'
						onClick={dismiss}
					/>
					<Button
						size='xs'
						h='26px'
						px={3}
						borderRadius='full'
						bg='accent.solid'
						color='text.onAccent'
						fontWeight='semibold'
						rightIcon={<Icon as={FiArrowRight} boxSize={3} />}
						_hover={{ bg: 'accent.hover' }}
						_active={{ bg: 'accent.pressed' }}
						onClick={handleOpen}
					>
						Open
					</Button>
				</Flex>
			</Flex>

			{/* countdown */}
			<Box h='3px' bg='bg.subtle' w='100%'>
				<Box ref={barRef} h='100%' bg='accent.solid' w='100%' />
			</Box>
		</Box>
	);
};

// Recipients are decided by the server (admin / superAdmin only), so this
// renders whatever alerts reach the store without a second role check.
const NewLeadAdminNotification = () => {
	const dispatch = useDispatch();
	const alerts = useSelector((state) => state.freshLead.adminLeadAlerts);
	const prevCountRef = useRef(0);

	const handleDismiss = useCallback(
		(id) => dispatch(removeAdminLeadAlert(id)),
		[dispatch],
	);

	useEffect(() => {
		if (alerts.length > prevCountRef.current) {
			playNotificationSound();
		}
		prevCountRef.current = alerts.length;
	}, [alerts.length]);

	if (alerts.length === 0) return null;

	return (
		<Box position='fixed' top='80px' right='16px' zIndex={9999}>
			{alerts.map((alert) => (
				<LeadAlertCard key={alert._id} alert={alert} onDismiss={handleDismiss} />
			))}
		</Box>
	);
};

export default NewLeadAdminNotification;
